import {
  PRIORITY,
  STATUS,
  ISSUETYPE,
  VERIFY_STATUS,
} from "../../../common/constant/constant";
import dayjs from "dayjs";

// Helper functions to convert between numeric values and display labels
export const getPriorityLabel = (priorityValue) => {
  const priorityMap = {
    [PRIORITY.LOW]: "Low",
    [PRIORITY.MEDIUM]: "Medium",
    [PRIORITY.HIGH]: "High",
  };
  return priorityMap[priorityValue] || "Unknown";
};

export const getVerifyStatusLabel = (verifyStatusValue) => {
  const verifyStatusMap = {
    [VERIFY_STATUS.COMPLETED]: "Completed",
    [VERIFY_STATUS.REOPEN]: "Reopen",
  };
  return verifyStatusMap[verifyStatusValue] || "Unknown";
};

export const getStatusLabel = (statusValue) => {
  const statusMap = {
    [STATUS.TODO]: "To Do",
    [STATUS.INPROGRESS]: "InProgress",
    [STATUS.QA]: "QA",
    [STATUS.PRODUCTION]: "Production",
    [STATUS.STAGING]: "Staging",
    [STATUS.PENDING]: "Pending",
    [STATUS.UAT]: "UAT",
  };
  return statusMap[statusValue] || "Unknown";
};

export const getIssueTypeLabel = (issueTypeValue) => {
  const issueTypeMap = {
    [ISSUETYPE.BUG]: "Bugs",
    [ISSUETYPE.TASK]: "Task",
    [ISSUETYPE.TESTING]: "Testing",
    [ISSUETYPE.DESIGNING]: "Designing",
    [ISSUETYPE.OTHERS]: "Others",
    [ISSUETYPE.MEETING]: "Meeting",
    [ISSUETYPE.DEPLOYMENT]: "Deployment",
  };
  return issueTypeMap[issueTypeValue] || "Unknown";
};

export const getIssueTypeFromLabel = (label) => {
  const issueTypeMap = {
    Bugs: ISSUETYPE.BUG,
    Task: ISSUETYPE.TASK,
    Testing: ISSUETYPE.TESTING,
    Meeting: ISSUETYPE.MEETING,
    Designing: ISSUETYPE.DESIGNING,
    Others: ISSUETYPE.OTHERS,
    Deployment: ISSUETYPE.DEPLOYMENT,
  };
  return issueTypeMap[label] || ISSUETYPE.TASK;
};

export const getPriorityFromLabel = (label) => {
  const priorityMap = {
    Low: PRIORITY.LOW,
    Medium: PRIORITY.MEDIUM,
    High: PRIORITY.HIGH,
  };
  return priorityMap[label] || PRIORITY.MEDIUM;
};

export const getStatusFromLabel = (label) => {
  const statusMap = {
    "To Do": STATUS.TODO,
    InProgress: STATUS.INPROGRESS,
    QA: STATUS.QA,
    Production: STATUS.PRODUCTION,
    Staging: STATUS.STAGING,
    Pending: STATUS.PENDING,
    UAT: STATUS.UAT,
  };
  return statusMap[label] || STATUS.TODO;
};

export function formatDate(dateString) {
  if (!dateString) return "No date";

  // Convert the date string to a valid Date object
  const date = new Date(
    dateString.includes("Z") ? dateString : dateString.replace(/\+00:00$/, "Z")
  );

  if (isNaN(date.getTime())) {
    console.error("Invalid date string:", dateString);
    return "Invalid date";
  }

  // Formatting options for UTC time
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC", // Ensures it stays in UTC
  };

  return date.toLocaleString("en-US", options);
}

export const minutesToHours = (min) => {
  const hours = String(Math.floor(min / 60)).padStart(2, "0");
  const minutes = String(min % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const getFileType = (filename) => {
  if (!filename) return;
  const extension = filename.split(".").pop().toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "image";
    case "mp4":
    case "webm":
    case "ogg":
      return "video";
    default:
      return "unsupported";
  }
};

export const getFileTypes = (filename) => {
  if (!filename) return 'unknown';

  const extension = filename.toLowerCase().split('.').pop();

  // Image files
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
  if (imageExtensions.includes(extension)) return 'image';

  // Video files
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
  if (videoExtensions.includes(extension)) return 'video';

  // Audio files
  const audioExtensions = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'];
  if (audioExtensions.includes(extension)) return 'audio';

  // Document files
  if (extension === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(extension)) return 'docx';
  if (['xls', 'xlsx'].includes(extension)) return 'excel';
  if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';

  // Archive files
  const archiveExtensions = ['zip', 'rar', 'tar', 'gz', '7z'];
  if (archiveExtensions.includes(extension)) return 'archive';

  // Code files
  const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'py', 'java', 'cpp', 'c', 'php', 'rb', 'json', 'xml'];
  if (codeExtensions.includes(extension)) return 'code';

  // Text files
  const textExtensions = ['txt', 'rtf', 'md'];
  if (textExtensions.includes(extension)) return 'text';

  return 'unknown';
};

export const formatDisplayDate = (dateString) => {
  return dayjs(dateString).format("MMM DD, YYYY");
};

export const formatDisplayTime = (timeString) => {
  return dayjs(timeString).format("hh:mm A");
};

export const calculateDuration = (startTime, endTime) => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  const durationMinutes = end.diff(start, "minute");
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  return `${hours}h ${minutes}m`;
};
